import { useEffect, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '@src/core/hooks';
import { getOrderSaleItems, getOrderSaleChangesAmount, type Order } from '@src/domains/orders';
import { resolveOrderDetailsFooter } from '@src/features/orders/core/resolveOrderDetailsFooter';
import { groupSaleItemsByPackageUnitId } from '@src/domains/cart';
import {
  loadOrderVouchers,
  loadOrderOriginalAmount,
  orderVoucherInfoSelector,
  orderOriginalAmountSelector,
} from '@src/features/orders/store/order';

export function useOrderDetails(order: Order) {
  const dispatch = useAppDispatch();
  const { hasMultipleVouchers } = useAppSelector(orderVoucherInfoSelector(order.id));
  const originalAmountCents = useAppSelector(orderOriginalAmountSelector(order.id));

  const saleChangeAmounts = useMemo(() => getOrderSaleChangesAmount(order), [order]);

  useEffect(() => {
    if (order) {
      dispatch(loadOrderVouchers(order));
    }
  }, []);

  useEffect(() => {
    if (saleChangeAmounts.length) {
      dispatch(loadOrderOriginalAmount(order.id));
    }
  }, [order.id, saleChangeAmounts]);

  const saleItems = useMemo(() => getOrderSaleItems(order), [order]);
  const { individualItems, packageGroup } = useMemo(() => groupSaleItemsByPackageUnitId(saleItems), [saleItems]);
  const footer = useMemo(() => resolveOrderDetailsFooter(order.status), [order.status]);

  return { packageGroups: packageGroup, individualItems, hasMultipleVouchers, footer, originalAmountCents };
}
